import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@mlm/ui';
import { CashierSidebar } from './shell/sidebar';

/**
 * Cashier shell. Collapsible sidebar on the left, content column on the right;
 * on narrow screens the sidebar becomes a hamburger + sheet driven by the same
 * nav data. Collapse state persists so the till keeps its preference across
 * reloads (this is a UI preference — the localStorage ban in CLAUDE.md is on
 * TOKENS).
 *
 * PRINTING: the receipt tape is `position: absolute; left:0; top:0` under
 * `@media print` (index.css hides `body *` and un-hides `.receipt-tape`). Do
 * NOT put `transform`, `filter` or `overflow: hidden` on any ancestor of
 * <main> — any of those makes that ancestor the tape's containing block and
 * shifts the print off the page. Plain flex + sticky is safe.
 */

const COLLAPSE_KEY = 'mlm.cashier.sidebar.collapsed';

export function Layout() {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    // Default EXPANDED on first load — a new cashier needs the labels.
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Close the mobile drawer on navigation.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <CashierSidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((v) => !v)}
        className="hidden md:flex h-screen sticky top-0"
      />

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[240px]">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <CashierSidebar collapsed={false} onNavigate={() => setMobileOpen(false)} className="h-full w-full border-r-0" />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="no-print md:hidden sticky top-0 z-10 border-b bg-background">
          <div className="flex h-14 items-center gap-3 px-4">
            <button
              type="button"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="font-semibold text-sm">Cashier POS</span>
          </div>
        </header>
        <main className="flex-1 px-4 md:px-6 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
